// Relevant / not relevant, on every one of Merlin's questions (kanban 3709db93).
//
// AI/Code check-in 2026-10-12. Scott: "let's start sending out the list of
// questions from Merlin to a small group of people ... even just like a
// quick: this is relevant, this is not relevant. And that gives us a metric."
// Two buttons, one tap each, for whoever reads the roadmap (the readers are
// the pilot group); the counts beside them are everyone's marks and the
// tooltip names who. Pressing the chosen one again clears it. Nothing else
// changes: a mark is a measurement, and reaches Merlin once as feedback.

import { useState } from 'react';
import type { QuestionIdentity, QuestionRelevance, RelevanceVerdict } from '../../services/roadmapReports';
import { marksTitle, nextVerdict } from './relevanceUtils';
import styles from './RoadmapV2.module.css';

export type MarkRelevanceFn = (id: QuestionIdentity, verdict: RelevanceVerdict | null) => Promise<void>;

interface Props {
  identity: QuestionIdentity;
  relevance: QuestionRelevance | null | undefined;
  onMark: MarkRelevanceFn;
}

function errorMessage(err: unknown): string {
  const res = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  if (typeof res?.data?.detail === 'string') return res.data.detail;
  return 'Could not save the mark — try again.';
}

export function RelevanceControls({ identity, relevance, onMark }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mine = relevance?.mine ?? null;
  const marks = relevance?.marks ?? [];
  const yes = relevance?.relevant ?? 0;
  const no = relevance?.not_relevant ?? 0;

  const press = async (clicked: RelevanceVerdict) => {
    setBusy(true);
    setError(null);
    try {
      await onMark(identity, nextVerdict(mine, clicked));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.relevance} data-testid="question-relevance" title={marksTitle(marks)}>
      <span className={styles.relevanceLead}>Relevant?</span>
      <button
        type="button"
        className={`${styles.relevanceBtn} ${mine === 'relevant' ? styles.relevanceOnYes : ''}`}
        onClick={() => void press('relevant')}
        disabled={busy}
        aria-pressed={mine === 'relevant'}
        aria-label={`Relevant (${yes})`}
        data-action="ask_mark_relevant"
        data-target-initiative-key={identity.initiative_key ?? undefined}
      >
        Yes{yes > 0 && <span className={styles.relevanceCount}>{yes}</span>}
      </button>
      <button
        type="button"
        className={`${styles.relevanceBtn} ${mine === 'not_relevant' ? styles.relevanceOnNo : ''}`}
        onClick={() => void press('not_relevant')}
        disabled={busy}
        aria-pressed={mine === 'not_relevant'}
        aria-label={`Not relevant (${no})`}
        data-action="ask_mark_not_relevant"
        data-target-initiative-key={identity.initiative_key ?? undefined}
      >
        No{no > 0 && <span className={styles.relevanceCount}>{no}</span>}
      </button>
      {error && <span className={styles.respondError} role="alert">{error}</span>}
    </div>
  );
}
